import React, { useRef } from 'react';
import { Cake } from 'lucide-react';
import MergeTagChips from '../../../components/marketing/MergeTagChips.jsx';

export const DEFAULT_BIRTHDAY = Object.freeze({
  sendTime: '10:00',
  body: 'Happy Birthday #firstname#! Wishing you a wonderful day celebrating with family. - #myagentname# with #company# (#mybusinessphonenumber#)',
  attachDigitalBusinessCard: false,
});

/** Format 'HH:MM' as '10:00 AM'. */
export function formatSendTime(hhmm) {
  const [h, m] = String(hhmm || '10:00').split(':').map(Number);
  const hour = ((h + 11) % 12) + 1;
  return `${hour}:${String(m || 0).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

/** Sample the message with made-up values so the agent sees what a client receives. */
export function previewBirthday(body) {
  return String(body || '')
    .replace(/#firstname#?/gi, 'Jane')
    .replace(/#myagentname#?/gi, 'Easton Padden')
    .replace(/#mybookinglink#?/gi, 'civixcrm.ai/book/easton')
    .replace(/#mybusinessphonenumber#?/gi, '(303) 555-0100')
    .replace(/#company#?/gi, 'Your Agency');
}

/**
 * Editor for a Birthday Drip: one greeting sent every year on the contact's birthday.
 * @param {{birthday: {sendTime: string, body: string, attachDigitalBusinessCard: boolean}, onChange: (b: object) => void,
 *          soldAuto: boolean, onSoldAuto: (v: boolean) => void}} props
 */
export default function BirthdayDripEditor({ birthday, onChange, soldAuto, onSoldAuto }) {
  const ref = useRef(null);
  const set = (patch) => onChange({ ...birthday, ...patch });
  return (
    <div className="panel p-4 space-y-4" data-testid="birthday-editor">
      <div className="flex items-center gap-2 font-semibold text-white">
        <Cake className="w-4 h-4 text-civix-purple" /> Annual Birthday Greeting
      </div>
      <p className="text-xs text-white/60" data-testid="birthday-recurring-note">
        This automated message will send every year on the contact's birthday indefinitely.
      </p>

      <label className="block text-[10px] uppercase tracking-wide text-white/40">
        Send at (contact's local time)
        <input
          type="time"
          value={birthday.sendTime}
          onChange={(e) => set({ sendTime: e.target.value || '10:00' })}
          data-testid="birthday-send-time"
          className="mt-1 block bg-civix-panelLight border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
        />
        <span className="normal-case text-white/40">Send at {formatSendTime(birthday.sendTime)} on the contact's birthday (never after 9 PM).</span>
      </label>

      <div className="space-y-1.5">
        <label className="text-[10px] uppercase tracking-wide text-white/40">Message</label>
        <textarea
          ref={ref}
          value={birthday.body}
          onChange={(e) => set({ body: e.target.value })}
          rows={4}
          maxLength={1600}
          data-testid="birthday-body"
          className="w-full bg-civix-panelLight border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono"
        />
        <MergeTagChips value={birthday.body} onChange={(v) => set({ body: v })} textareaRef={ref} testId="birthday-tag-chips" />
        <div className="rounded-md bg-white/5 px-3 py-2 text-xs text-white/60" data-testid="birthday-preview">
          <span className="text-white/35">Preview: </span>{previewBirthday(birthday.body)}
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer">
        <input type="checkbox" className="accent-civix-purple" checked={birthday.attachDigitalBusinessCard}
          onChange={(e) => set({ attachDigitalBusinessCard: e.target.checked })} />
        Attach my digital business card link
      </label>

      <label className="flex items-start gap-2 text-sm text-white/80 cursor-pointer" data-testid="birthday-sold-auto">
        <input type="checkbox" className="mt-0.5 accent-civix-purple" checked={soldAuto} onChange={(e) => onSoldAuto(e.target.checked)} />
        <span>
          Automatically add clients to this drip when they're marked <b>Sold</b>
          <span className="block text-xs text-white/45">Other contacts join from the leads list: select them → "Enroll in birthday texts".</span>
        </span>
      </label>
    </div>
  );
}
